/**
 * A receipt that runs over several files — TRBUD-352.
 *
 * Eden, 8 October 2026: *"1 Purchase Order had multiple receipt images/pdfs
 * associated with it. the system did not like that 1 receipt was split amongst
 * multiple images so we need a way to log/indicate that an receipt is a
 * continuation of another. it can literally just be a check box that if checked
 * the receipt should find its matching invoice number and log it like it was all
 * one image"*.
 *
 * ## The words
 *
 * - **Primary**: the queue item a receipt is logged from. Its own file is page 1.
 * - **Page** (continuation): a later file of that receipt. It keeps its own
 *   document and its own queue item, which is how it leaves the queue when it is
 *   joined and comes back when the join is undone, with nothing deleted.
 * - **Marked**: a file somebody said is a continuation, not yet joined.
 * - **Joined**: a marked file linked to its primary.
 *
 * The plan is docs/plans/trbud-352-receipt-continuation.md.
 *
 * ## Why this imports FROM `requests.ts` and never the other way
 *
 * `requests.ts` builds zod schemas at module load. A cycle between the two would
 * be the `MAX_WORK_ORDER_FAULTS` ReferenceError CLAUDE.md records: a const read
 * before its module finished evaluating. So `MAX_RECEIPT_PAGES` lives there and
 * is imported here.
 *
 * Browser-safe: types, zod schemas and pure functions.
 */
import { z } from "zod";
import { fromCents, toCents } from "./money.js";
import { MAX_RECEIPT_PAGES, idempotencyKey, strictObject } from "./requests.js";
import type {
  AiProposal,
  AiProposalAsset,
  AiProposalCheck,
  AiProposalField,
  AiProposalLine,
  AiProposalStatus,
} from "./receipt-parser.js";
import { isLoggableDocClass, type DocClass } from "./vocabulary.js";

/** How a page came to be joined: the AI's read number, a number a person typed, or a pick. */
export const receiptJoinBases = ["invoice_number_read", "invoice_number_typed", "picked"] as const;
export type ReceiptJoinBasis = (typeof receiptJoinBases)[number];

/** The longest invoice number a person may type: `createManualInvoiceBody.invoiceNumber`'s bound. */
export const RECEIPT_INVOICE_NUMBER_MAX = 100;

/**
 * An invoice number as two papers are compared on it.
 *
 * Trimmed, upper-cased, with whitespace and `#` dropped, because those are how
 * the same number is printed differently on two pages ("Inv # 1234" against
 * "INV#1234"). Dashes and prefixes are KEPT: "INV-1234" is not "1234", and
 * treating them as one would join two different bills.
 *
 * Null for nothing at all, so "no number" is never a number that matches.
 */
export function normaliseInvoiceNumber(raw: string | null | undefined): string | null {
  if (typeof raw !== "string") return null;
  const value = raw.trim().toUpperCase().replace(/[\s#]+/g, "");
  return value === "" ? null : value;
}

/* ── wire shapes ────────────────────────────────────────────────────── */

/** One file of a receipt, as the Review screen lists and shows it. Page 1 is the primary's own file. */
export interface ReceiptPage {
  pageNumber: number;
  /** That file's own queue item (its manual extraction). */
  itemId: string;
  documentId: string;
  /** The file's original name, else its id. */
  documentLabel: string;
  mimeType: string;
  /** `documentDelivery(...) === "inline"`, as `ReviewDetail.canPreview`. */
  canPreview: boolean;
  receivedAt: string;
  /** Who joined it. Null for page 1, which nobody joined. */
  joinedByLabel: string | null;
  joinedAt: string | null;
  matchBasis: ReceiptJoinBasis | null;
  matchedInvoiceNumber: string | null;
  /** The newest AI read of this page, or null when nothing read it. */
  aiStatus: AiProposalStatus | null;
}

/** This item as a page of another receipt: marked and waiting, or joined. */
export interface ReceiptContinuation {
  state: "unmatched" | "joined";
  /** What a person typed when they marked it. */
  statedInvoiceNumber: string | null;
  /** Its own newest completed AI read's number, normalised. */
  readInvoiceNumber: string | null;
  markedByLabel: string | null;
  joinedTo: {
    /** The receipt's queue item, which Review opens. */
    itemId: string;
    title: string;
    parentLabel: string | null;
    /** This file's page number in that receipt. */
    pageNumber: number;
  } | null;
  /** False once the receipt it is joined to is logged: its pages are then fixed. */
  canUndo: boolean;
}

/** A receipt this page could be joined to. Always a PRIMARY: a joined page is folded into its receipt. */
export interface ReceiptJoinCandidate {
  itemId: string;
  /** "Invoice 1234" from the read, else the file name. */
  title: string;
  readInvoiceNumber: string | null;
  /** The searched number this candidate matched on, or null. */
  matchedInvoiceNumber: string | null;
  /** From the read. */
  vendorName: string | null;
  /** WO-000829, MaintainX PO 20, ... */
  parentLabel: string | null;
  receivedAt: string;
  pageCount: number;
  /** Shares a live work order or purchase order with the page. */
  sameParent: boolean;
  /** Same invoice number, and the two reads do not name different vendors. */
  numberMatches: boolean;
  /** Both reads name the same vendor; false when they name different ones; null when either is unread. */
  vendorAgrees: boolean | null;
}

export type ReceiptJoinRefusalReason = "no_number" | "no_match" | "ambiguous" | "vendor_unconfirmed";

export interface ReceiptJoinCandidatesResponse {
  /** What was searched, normalised. */
  invoiceNumbers: string[];
  candidates: ReceiptJoinCandidate[];
  /**
   * The candidate to pre-select: the one automatic joining would pick, else the
   * only unentered receipt on the same job or order. Never joined by itself on
   * the second ground; a person presses Join.
   */
  suggestion: string | null;
  /** Why automatic joining would not join, or null when it would. */
  why: ReceiptJoinRefusalReason | null;
}

/** A file's place in a receipt of several files. Null on a one-file receipt and on an unmatched page. */
export interface ReceiptMembership {
  /** The RECEIPT's queue item (the primary's), which Review opens. */
  itemId: string;
  /** "Invoice 1234" once logged, else the read number, else page 1's file name. */
  receiptTitle: string;
  /** 1 for the primary's own file. */
  pageNumber: number;
  pageCount: number;
}

/** What marking, joining or undoing answers with. */
export interface ReceiptContinuationReceipt {
  itemId: string;
  state: "unmatched" | "joined" | "cleared";
  joinedTo: ReceiptContinuation["joinedTo"];
  /** Only on a mark: what the automatic join attempt did, after the mark committed. */
  autoJoin: { outcome: "joined" | ReceiptJoinRefusalReason | "refused" } | null;
}

/**
 * What putting a receipt's pages in a new order answers with — TRBUD-378.
 *
 * `receiptItemId` is the receipt's queue item AFTER the move. It differs from
 * `previousReceiptItemId` when another file was made page 1: page 1 is always
 * the receipt's own item, so that file's item became the receipt, and the
 * screen opens it.
 */
export interface ReceiptPagesOrderReceipt {
  receiptItemId: string;
  previousReceiptItemId: string;
  /** The receipt's files in their new order. */
  pages: ReceiptPage[];
}

/* ── request schemas ────────────────────────────────────────────────── */

const typedInvoiceNumber = z.string().trim().min(1).max(RECEIPT_INVOICE_NUMBER_MAX);

/** `POST /review/:itemId/continuation`: mark this file as a later page. */
export const markReceiptContinuationBody = strictObject({
  invoiceNumber: typedInvoiceNumber.nullish(),
  idempotencyKey,
});
export type MarkReceiptContinuationBody = z.infer<typeof markReceiptContinuationBody>;

/** `POST /review/:itemId/continuation/join`: join this page to a receipt. */
export const joinReceiptContinuationBody = strictObject({
  primaryItemId: z.string().uuid(),
  invoiceNumber: typedInvoiceNumber.nullish(),
  idempotencyKey,
});
export type JoinReceiptContinuationBody = z.infer<typeof joinReceiptContinuationBody>;

/**
 * `POST /review/:itemId/continuation/undo`. `keepMarked` true takes the page off
 * its receipt and leaves it waiting for the right one; false clears the mark.
 */
export const undoReceiptContinuationBody = strictObject({
  keepMarked: z.boolean(),
  idempotencyKey,
});
export type UndoReceiptContinuationBody = z.infer<typeof undoReceiptContinuationBody>;

/**
 * `POST /review/:itemId/pages/order` — TRBUD-378. `:itemId` is the receipt's
 * own queue item (page 1). `pageItemIds` is EVERY file of the receipt in its new
 * order, page 1 first, each named by its own queue item. Naming another file
 * first makes that file page 1 (plan decision 1).
 *
 * The rule a zod refinement would carry, that no file is named twice, is
 * `reorderReceiptPagesRefusal` instead, as `manualInvoiceAiPagesRefusal` is.
 */
export const reorderReceiptPagesBody = strictObject({
  pageItemIds: z.array(z.string().uuid()).min(2).max(MAX_RECEIPT_PAGES),
  idempotencyKey,
});
export type ReorderReceiptPagesBody = z.infer<typeof reorderReceiptPagesBody>;

/** A sentence when the new order names a file twice, or null when it is fine. */
export function reorderReceiptPagesRefusal(body: { pageItemIds: readonly string[] }): string | null {
  return new Set(body.pageItemIds.map((id) => id.toLowerCase())).size === body.pageItemIds.length
    ? null
    : "The new order names the same page twice.";
}

/** `GET /review/:itemId/continuation/candidates`. */
export const receiptJoinCandidatesQuery = strictObject({
  invoiceNumber: typedInvoiceNumber.optional(),
});
export type ReceiptJoinCandidatesQuery = z.infer<typeof receiptJoinCandidatesQuery>;

/**
 * The two rules on a manual-invoice body's AI fields that a zod refinement
 * cannot carry: `createPurchaseOrderBillBody` is `createManualInvoiceBody.omit`,
 * and zod refuses `.omit()` on a refined object. So both log routes ask this
 * (the purchase-order bill carries the AI fields too since 9 October 2026), and
 * the tests do too. A sentence, or null when the body is fine.
 */
export function manualInvoiceAiPagesRefusal(body: {
  aiExtractionId?: string | null;
  aiPages?: readonly { page: number; extractionId: string }[] | null;
}): string | null {
  const pages = body.aiPages ?? null;
  if (pages === null) return null;
  if (body.aiExtractionId) {
    return "Send aiExtractionId for a one-file receipt or aiPages for a receipt of several files, not both.";
  }
  if (new Set(pages.map((p) => p.page)).size !== pages.length) {
    return "aiPages names the same page twice.";
  }
  if (new Set(pages.map((p) => p.extractionId)).size !== pages.length) {
    return "aiPages names the same AI read for two pages.";
  }
  return null;
}

/* ── the upload's two fields ────────────────────────────────────────── */

/**
 * The two multipart fields an upload may carry: `continuation` ("true" or
 * "false") and `continuationInvoiceNumber`.
 *
 * Refused rather than ignored, the body-strictness rule: a typo in either would
 * otherwise upload the file as a receipt of its own, silently. Only an invoice
 * or a receipt can continue another receipt, which also refuses it on a photo.
 */
export function parseUploadContinuation(input: {
  continuation: string | null;
  invoiceNumber: string | null;
  docClass: DocClass;
}): { ok: true; continuation: boolean; invoiceNumber: string | null } | { ok: false; refusal: string } {
  const flag = input.continuation === null ? null : input.continuation.trim().toLowerCase();
  if (flag !== null && flag !== "true" && flag !== "false") {
    return { ok: false, refusal: 'The "continuation" field must be true or false.' };
  }
  const continuation = flag === "true";
  const number = input.invoiceNumber === null ? null : input.invoiceNumber.trim();
  if (number !== null && number !== "" && !continuation) {
    return {
      ok: false,
      refusal: "An invoice number for a later page was sent, but the file is not marked as a later page.",
    };
  }
  if (number !== null && number.length > RECEIPT_INVOICE_NUMBER_MAX) {
    return { ok: false, refusal: `An invoice number can be at most ${RECEIPT_INVOICE_NUMBER_MAX} characters.` };
  }
  if (continuation && !isLoggableDocClass(input.docClass)) {
    return { ok: false, refusal: "Only an invoice or a receipt can continue another receipt." };
  }
  return { ok: true, continuation, invoiceNumber: continuation && number ? number : null };
}

/* ── automatic joining ──────────────────────────────────────────────── */

/**
 * The single receipt to join a page to, or why there is none. The server and
 * the tests both use it, so the rule is stated once.
 *
 * 1. No number at all: no join.
 * 2. Number matches among the candidates (already folded to primaries): none is
 *    `no_match`, more than one is `ambiguous`.
 * 3. Exactly one: joined when it shares a live work order or purchase order with
 *    the page; otherwise only when both reads name the same vendor.
 *
 * Never joined: the only unentered receipt on the same job when no number
 * matches. That is likely right and still a guess (page 1 may not be uploaded
 * yet), so the picker pre-selects it and a person presses Join. Eden's ticket:
 * "If none or several match, the person picks or types the invoice number;
 * nothing is guessed silently."
 */
export function autoJoinTarget(
  candidates: readonly ReceiptJoinCandidate[],
  numbers: readonly string[],
): { itemId: string; invoiceNumber: string } | { reason: ReceiptJoinRefusalReason } {
  if (numbers.length === 0) return { reason: "no_number" };
  const matches = new Map<string, ReceiptJoinCandidate>();
  for (const candidate of candidates) {
    if (candidate.numberMatches) matches.set(candidate.itemId, candidate);
  }
  if (matches.size === 0) return { reason: "no_match" };
  if (matches.size > 1) return { reason: "ambiguous" };
  const [only] = [...matches.values()];
  if (!only) return { reason: "no_match" };
  if (!only.sameParent && only.vendorAgrees !== true) return { reason: "vendor_unconfirmed" };
  const invoiceNumber =
    only.matchedInvoiceNumber ?? normaliseInvoiceNumber(only.readInvoiceNumber) ?? numbers[0]!;
  return { itemId: only.itemId, invoiceNumber };
}

/* ── one proposal for a receipt of several pages ────────────────────── */

/** How many lines one page may contribute: `extraction.ts`'s `max(500)`, rounded up. */
export const RECEIPT_PAGE_LINE_SPAN = 1000;

/**
 * A combined line's number: `page * 1000 + pageLine`. Stable while further pages
 * finish reading, because a page never renumbers another page's lines.
 */
export function receiptLineKey(page: number, pageLine: number): number {
  return page * RECEIPT_PAGE_LINE_SPAN + pageLine;
}

/** The page and the page's own line number back out of a combined line number. */
export function splitReceiptLineKey(key: number): { page: number; line: number } {
  return { page: Math.floor(key / RECEIPT_PAGE_LINE_SPAN), line: key % RECEIPT_PAGE_LINE_SPAN };
}

export interface CombinedAiProposalPage {
  page: number;
  documentId: string;
  /** That page's own proposal extraction, or null when nothing read it. */
  extractionId: string | null;
  status: AiProposalStatus | null;
}

/**
 * The proposal for a receipt of several pages: one AI proposal per page, read
 * as one. `extractionId` is `receipt:<primaryItemId>`, stable and never sent
 * to the server; the save sends each page's own extraction instead (`aiPages`).
 */
export interface CombinedAiProposal extends AiProposal {
  pages: CombinedAiProposalPage[];
}

export function isCombinedAiProposal(proposal: AiProposal | null | undefined): proposal is CombinedAiProposal {
  return proposal !== null && proposal !== undefined && Array.isArray((proposal as Partial<CombinedAiProposal>).pages);
}

export interface PageProposalInput {
  page: number;
  documentId: string;
  proposal: AiProposal | null;
}

function firstField<T>(
  pages: readonly { proposal: AiProposal }[],
  pick: (proposal: AiProposal) => AiProposalField<T | null>,
): AiProposalField<T | null> | null {
  for (const { proposal } of pages) {
    const value = pick(proposal);
    if (value.value !== null) return value;
  }
  return null;
}

function emptyField<T>(): AiProposalField<T | null> {
  return { value: null, confidence: null, flagged: false };
}

/**
 * One proposal from every page's own read — TRBUD-352 section 3.6.
 *
 * - **Header** from page 1, else the first later page that has the value; the
 *   **total from the LAST page that states one**, because a grand total prints
 *   on the last page.
 * - **Lines** from every page in page order, each numbered `page * 1000 + line`.
 * - **Checks**: each page's, prefixed `Page N:`, EXCEPT its arithmetic (one
 *   page never adds up to the receipt), plus one arithmetic check over every
 *   line against the combined total, plus a sentence for pages that read
 *   different vendors or numbers, a page still being read, a page that failed,
 *   a page nothing read, and pages read by different parser versions.
 * - **Status** is `pending` while ANY page is still being read, so the form does
 *   not pre-fill from pages 1 and 3 and then have page 2 arrive; a FAILED page
 *   does not hold the others back.
 *
 * A one-page item keeps its own proposal exactly. Null when no page has one.
 */
export function combinePageProposals(
  primaryItemId: string,
  pages: readonly PageProposalInput[],
): AiProposal | CombinedAiProposal | null {
  const ordered = [...pages].sort((a, b) => a.page - b.page);
  if (ordered.length <= 1) return ordered[0]?.proposal ?? null;
  const read = ordered.filter((p): p is PageProposalInput & { proposal: AiProposal } => p.proposal !== null);
  if (read.length === 0) return null;
  const completed = read.filter((p) => p.proposal.status === "completed");
  const pending = read.filter((p) => p.proposal.status === "pending");
  const failed = read.filter((p) => p.proposal.status === "failed");
  const status: AiProposalStatus =
    pending.length > 0 ? "pending" : completed.length > 0 ? "completed" : "failed";
  const lead = completed[0] ?? read[0]!;

  const vendorPage = completed.find((p) => p.proposal.vendorName.value !== null) ?? null;
  const vendorName = vendorPage?.proposal.vendorName ?? emptyField<string>();
  const vendorId = vendorPage?.proposal.vendorId ?? null;
  const invoiceNumber = firstField(completed, (p) => p.invoiceNumber) ?? emptyField<string>();
  const invoiceDate = firstField(completed, (p) => p.invoiceDate) ?? emptyField<string>();
  const serviceDays = new Set(completed.map(p => p.proposal.serviceDate?.value).filter((day): day is string => Boolean(day)));
  const servicePage = serviceDays.size === 1 ? completed.find(p => p.proposal.serviceDate?.value) : undefined;
  const currencies = [...new Set(completed.map((p) => p.proposal.currency?.trim().toUpperCase()).filter((value): value is string => Boolean(value)))];
  const currenciesDisagree = currencies.length > 1;
  const currency = currenciesDisagree ? null : currencies[0] ?? null;
  // A last-page total is meaningful only when all stated currencies agree.
  // Keep each original read intact; suppress money only in this combined view.
  const totalAmount = currenciesDisagree ? emptyField<string>() :
    firstField([...completed].reverse(), (p) => p.totalAmount) ?? emptyField<string>();

  const lines: AiProposalLine[] = [];
  for (const { page, proposal } of completed) {
    for (const line of proposal.lines) {
      lines.push({ ...line, lineNumber: receiptLineKey(page, line.lineNumber),
        serviceDate: line.serviceDate ?? proposal.serviceDate?.value ?? null,
        serviceDateLabel: line.serviceDateLabel ?? proposal.serviceDateLabel ?? null,
        ...(currenciesDisagree ? { amount: null, unitCost: null } : {}),
      });
    }
  }

  const checks: AiProposalCheck[] = [];
  if (currenciesDisagree) {
    checks.push({
      check: "pages_disagree_currency",
      message: `The pages name different currencies (${currencies.join(", ")}). Check every page belongs to this receipt and enter the actual USD charges; no monetary figures have been filled in.`,
    });
  }
  const pageWarnings: string[] = [];
  for (const { page, proposal } of completed) {
    for (const check of proposal.checks) {
      if (check.check === "arithmetic") continue;
      checks.push({ check: check.check, message: `Page ${page}: ${check.message}` });
    }
    for (const warning of proposal.warnings) {
      if (proposal.checks.some((check) => check.message === warning)) continue;
      pageWarnings.push(`Page ${page}: ${warning}`);
    }
  }

  // One arithmetic check over the whole receipt, as the worker's check 4 does for
  // one file: a DISCOUNT counts negative, within two cents, and nothing is said
  // when it cannot be checked (no total, or a line with no amount).
  if (totalAmount.value !== null && lines.length > 0 && lines.every((line) => line.amount !== null)) {
    let sum = 0;
    for (const line of lines) {
      const cents = Math.abs(toCents(line.amount!));
      sum += line.kind === "DISCOUNT" ? -cents : cents;
    }
    const total = toCents(totalAmount.value);
    if (Math.abs(sum - total) > 2) {
      checks.push({
        check: "arithmetic",
        message:
          `The lines on every page add to ${fromCents(sum)} but the total reads ` +
          `${fromCents(total)}, a difference of ${fromCents(sum - total)}.`,
      });
    }
  }

  const distinctVendors = new Set(
    completed.map((p) => p.proposal.vendorName.value?.trim().toLowerCase()).filter((v): v is string => Boolean(v)),
  );
  if (distinctVendors.size > 1) {
    checks.push({
      check: "pages_disagree_vendor",
      message: "The pages name different vendors. Check every page is from the same receipt.",
    });
  }
  const distinctNumbers = new Set(
    completed
      .map((p) => normaliseInvoiceNumber(p.proposal.invoiceNumber.value))
      .filter((v): v is string => v !== null),
  );
  if (distinctNumbers.size > 1) {
    checks.push({
      check: "pages_disagree_invoice_number",
      message: "The pages show different invoice numbers. Check every page is from the same receipt.",
    });
  }
  for (const { page } of pending) {
    checks.push({ check: "page_pending", message: `Page ${page} is still being read.` });
  }
  for (const { page, proposal } of failed) {
    checks.push({
      check: "page_failed",
      message: `Page ${page} could not be read: ${proposal.failureReason ?? "the parser could not read this file."}`,
    });
  }
  for (const { page, proposal } of ordered) {
    if (proposal === null) {
      checks.push({ check: "page_unread", message: `Page ${page} has not been read by the AI.` });
    }
  }
  const versions = new Set(completed.map((p) => `${p.proposal.parserName}@${p.proposal.parserVersion}`));
  if (versions.size > 1) {
    checks.push({
      check: "pages_parser_versions",
      message: "The pages were read by different parser versions.",
    });
  }

  const confidences = completed
    .map((p) => p.proposal.confidence)
    .filter((c): c is number => typeof c === "number");
  const assets: AiProposalAsset[] = [];
  const seenAssets = new Set<string>();
  for (const { proposal } of completed) {
    for (const asset of proposal.assets) {
      const key = `${asset.unitNumber ?? ""}|${asset.vin ?? ""}`;
      if (seenAssets.has(key)) continue;
      seenAssets.add(key);
      assets.push(asset);
    }
  }

  return {
    extractionId: `receipt:${primaryItemId}`,
    parserName: lead.proposal.parserName,
    parserVersion: lead.proposal.parserVersion,
    isStub: read.some((p) => p.proposal.isStub),
    threshold: lead.proposal.threshold,
    status,
    reviewed: completed.length > 0 && completed.every((p) => p.proposal.reviewed),
    failureReason:
      status === "failed" ? (lead.proposal.failureReason ?? "The parser could not read these files.") : null,
    confidence: status === "completed" && confidences.length > 0 ? Math.min(...confidences) : null,
    flagged: completed.some((p) => p.proposal.flagged),
    vendorName,
    vendorId,
    // TRBUD-412: the shop that did the work, from the first page that names one.
    performedByName: completed.find((p) => p.proposal.performedByName)?.proposal.performedByName ?? null,
    performedById: completed.find((p) => p.proposal.performedByName)?.proposal.performedById ?? null,
    invoiceNumber,
    invoiceDate,
    serviceDate: servicePage?.proposal.serviceDate ?? emptyField<string>(),
    serviceDateLabel: servicePage?.proposal.serviceDateLabel ?? null,
    currency,
    totalAmount,
    lines,
    flaggedCount: completed.reduce((sum, p) => sum + p.proposal.flaggedCount, 0),
    warnings: [...checks.map((check) => check.message), ...pageWarnings],
    checks,
    assets,
    newerAttempt: null,
    pages: ordered.map(({ page, documentId, proposal }) => ({
      page,
      documentId,
      extractionId: proposal?.extractionId ?? null,
      status: proposal?.status ?? null,
    })),
  };
}
